import { useTranslation } from 'react-i18next';

import type { CardPreflightRecap } from '../../../api/cards';
import { cardMessageText } from '../../../api/cards';
import { ErrorMessage } from '../../common/ErrorMessage';

export function RecapPanel({
  recap,
  canAdjust,
  onBack,
  onCancel,
  onContinue,
}: {
  recap: CardPreflightRecap;
  /** False when there is no saved form to go back to (a resume in a fresh tab). */
  canAdjust: boolean;
  onBack: () => void;
  onCancel: () => void;
  onContinue: () => void;
}) {
  const { t } = useTranslation();
  const hasErrors = recap.errors.length > 0;
  const canContinue = recap.valid.length > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 border border-green/40 bg-green/12 px-3 py-2 text-sm text-green-ink">
        <svg
          className="size-4 shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
        {t('cardGenerator.recap.ready', {
          valid: recap.valid.length,
          total: recap.total,
        })}
      </div>

      {hasErrors && (
        <div className="overflow-hidden border border-rule">
          <div className="bg-magenta/6 px-3 py-2 text-xs font-medium tracking-wide text-magenta uppercase">
            {t('cardGenerator.recap.skipped', { count: recap.errors.length })}
          </div>
          <div className="epi-table-scroll">
            <table className="epi-table w-full">
              <thead>
                <tr>
                  <th>{t('cardGenerator.results.upn')}</th>
                  <th>{t('cardGenerator.results.name')}</th>
                  <th>{t('cardGenerator.results.status')}</th>
                </tr>
              </thead>
              <tbody>
                {recap.errors.map((e) => (
                  <tr key={e.upn}>
                    <td className="max-w-[180px] truncate font-mono text-xs text-muted">
                      {e.upn}
                    </td>
                    <td className="text-ink">{e.displayName ?? '—'}</td>
                    <td>
                      <span
                        className="text-xs text-magenta"
                        title={cardMessageText(t, e)}
                      >
                        {cardMessageText(t, e) || t('common.error')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!canContinue && (
        <ErrorMessage message={t('cardGenerator.recap.noneValid')} />
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onContinue}
          disabled={!canContinue}
          className="btn-primary flex-1 justify-center disabled:cursor-not-allowed disabled:opacity-40"
        >
          {t('cardGenerator.recap.continue', { count: recap.valid.length })}
        </button>
        {canAdjust && (
          <button type="button" onClick={onBack} className="btn-ghost">
            {t('cardGenerator.recap.back')}
          </button>
        )}
        <button type="button" onClick={onCancel} className="btn-ghost">
          {t('cardGenerator.recap.cancel')}
        </button>
      </div>
    </div>
  );
}
